import { useRef, useState } from "react";
import { UploadCloud, FileImage, PencilRuler, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { PRESETS, presetRooms } from "@/lib/estimate";

export default function UploadStep({ onRooms }) {
  const input = useRef();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drag, setDrag] = useState(false);

  const analyze = async (file) => {
    if (!file) return;
    if (!/image\/(png|jpe?g|webp)/.test(file.type)) return toast.error("Please upload a JPG, PNG or WEBP image");
    setPreview(URL.createObjectURL(file));
    setLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/analyze-floorplan", fd);
      toast.success(`Detected ${data.rooms.length} rooms${data.bhk ? ` · ${data.bhk}` : ""}`);
      onRooms(data.rooms, "ai", data);
    } catch (e) {
      toast.error(formatErr(e));
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6" data-testid="upload-step">
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); analyze(e.dataTransfer.files[0]); }}
        onClick={() => !loading && input.current.click()}
        data-testid="floorplan-dropzone"
        className={`lg:col-span-3 relative rounded-2xl border-2 border-dashed cursor-pointer min-h-[380px] grid place-items-center overflow-hidden transition-colors ${drag ? "border-brand-accent bg-brand-accent/5" : "border-brand-line bg-brand-cream hover:border-brand-accent"}`}>
        <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="floorplan-file-input" onChange={(e) => analyze(e.target.files[0])} />
        {preview ? (
          <>
            <img src={preview} alt="Uploaded floor plan" className="absolute inset-0 w-full h-full object-contain p-6 opacity-70" />
            {loading && (
              <>
                <div className="scanline" />
                <div className="relative bg-brand-cream/90 backdrop-blur-md rounded-xl px-6 py-4 flex items-center gap-3 shadow-lg" data-testid="analyzing-indicator">
                  <Loader2 className="w-5 h-5 animate-spin text-brand-accent" />
                  <div>
                    <p className="text-sm text-brand-ink">AI is reading your floor plan…</p>
                    <p className="text-xs text-brand-muted">Detecting rooms & measuring areas (~30s)</p>
                  </div>
                </div>
              </>
            )}
          </>
        ) : (
          <div className="text-center px-8">
            <div className="mx-auto w-16 h-16 rounded-full bg-brand-sand grid place-items-center"><UploadCloud className="w-7 h-7 text-brand-accent" strokeWidth={1.5} /></div>
            <p className="font-serif-display text-3xl text-brand-ink mt-6">Drop your floor plan here</p>
            <p className="text-sm text-brand-muted mt-2">or click to browse · JPG, PNG, WEBP up to 10MB</p>
            <p className="text-xs text-brand-muted-light mt-6 inline-flex items-center gap-2"><FileImage className="w-3.5 h-3.5" /> Builder brochures & phone photos work great</p>
          </div>
        )}
      </div>
      <div className="lg:col-span-2 rounded-2xl bg-brand-sand border border-brand-line p-8">
        <PencilRuler className="w-6 h-6 text-brand-accent" strokeWidth={1.5} />
        <h3 className="font-serif-display text-3xl text-brand-ink mt-5">No floor plan handy?</h3>
        <p className="text-sm text-brand-muted mt-2">Start from a layout and adjust room sizes yourself.</p>
        <div className="mt-6 space-y-3">
          {Object.keys(PRESETS).map((k) => (
            <button key={k} onClick={() => onRooms(presetRooms(k), "manual")} data-testid={`preset-${k.replace(" ", "").toLowerCase()}-btn`}
              className="w-full text-left rounded-xl bg-brand-cream border border-brand-line px-5 py-4 hover:border-brand-accent transition-colors flex justify-between items-center">
              <span className="text-brand-ink">{k} Apartment</span><span className="text-xs text-brand-muted-light">{PRESETS[k].length} rooms</span>
            </button>
          ))}
          <button onClick={() => onRooms([], "manual")} data-testid="preset-blank-btn" className="w-full text-sm text-brand-accent hover:text-brand-accent-dark py-2">Start from scratch →</button>
        </div>
      </div>
    </div>
  );
}
